import type {RequestMethod} from '#lit/common/constants/index.js';
import type {Nullable} from '#lit/types/general.js';

import {getCSRFToken} from './auth.js';
import {namespaceIdGenerator} from './generators.js';

/**
 * Simple interface for fetcher parameters.
 */
export interface FetcherParams<K = unknown> {
  method: RequestMethod;
  body?: K | Record<string, unknown> | unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  responseParser?: 'json' | 'formdata' | 'text' | 'blob' | 'url' | null;
}

export function getQueryParameter<T extends string>(key: string): Nullable<T> {
  const queryParams = new URLSearchParams(location.search);
  if (!queryParams.has(key)) return null;
  return queryParams.get(key) as T;
}

/**
 * Simple fetch wrap to make native fetch more usable.
 */
export async function fetcher<T, K = unknown>(
  url: string,
  params: FetcherParams<K>
): Promise<T> {
  const headers: Record<string, string> | undefined =
    params.body instanceof FormData
      ? undefined
      : {'Content-Type': 'application/json'};

  if (headers && getCSRFToken()) {
    headers['X-CSRF-Token'] = getCSRFToken();
  }

  const requestHeaders = new Headers({...headers, ...params.headers});
  if (
    (params.responseParser === undefined || params.responseParser === 'json') &&
    !requestHeaders.has('Accept')
  ) {
    requestHeaders.set('Accept', 'application/json');
  }

  const requestData: RequestInit = {
    signal: params.signal,
    method: params.method,
    headers: requestHeaders,
  };

  if (params.body) {
    requestData.body =
      params.body instanceof FormData
        ? params.body
        : JSON.stringify(params.body);
  }

  try {
    const res = await fetch(url, requestData);
    let data;
    switch (params.responseParser) {
      case null:
        data = res.ok ? '' : await res.json();
        break;
      case 'formdata':
        data = await res.formData();
        break;
      case 'blob':
        data = await res.blob();
        break;
      case 'text':
        data = await res.text();
        break;
      case 'url':
        data = res.redirected ? res.url : '';
        break;
      default:
        data = await res.json();
        break;
    }

    if (res.ok) {
      return Promise.resolve(data);
    } else {
      return Promise.reject(data);
    }
  } catch (err) {
    console.error(err);
    return Promise.reject(err);
  }
}

export type URLBuilderParams = Record<
  string,
  (string | number)[] | string | number
>;

/**
 * Creates a simple URL given a path string.
 */
export function buildURL(pathStr: string, params: URLBuilderParams = {}): URL {
  const baseURL = `${location.protocol}//${location.host}`;
  const url = new URL(baseURL + pathStr);

  Object.keys(params).forEach(k => {
    const v = params[k];

    if (typeof v === 'string' || typeof v === 'number') {
      url.searchParams.set(k, String(v));
    }

    if (Array.isArray(v)) {
      v.forEach(value => {
        url.searchParams.append(`${k}[]`, String(value));
      });
    }
  });

  return url;
}

/**
 * Get default headers for requests.
 */
export function getDefaultHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCSRFToken(),
  };
}

export interface RequestExecutionHandler {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (signal: AbortSignal, ...args: any[]): Promise<unknown | void>;
  allowConcurrentRequests?: boolean;
}

export type RequestMetadata = {
  aborted: boolean;
  timestamp: Date;
  controller: AbortController;
};

export type RequestState = {
  executionHandler: RequestExecutionHandler;
  currentRequest?: string;
  requests: Record<string, RequestMetadata>;
};

export class RequestHandler {
  protected idGenerator: IterableIterator<string>;
  private requestStateMap: Map<string, RequestState> = new Map();

  private executionHooks: ((state?: RequestState) => void)[] = [];

  private constructor(
    public requestExecutionHandlers: Map<string, RequestExecutionHandler>
  ) {
    this.idGenerator = namespaceIdGenerator('request');
    this.requestExecutionHandlers = requestExecutionHandlers;
    this.registerRequests();
  }

  addExecutionHandler(key: string, handler: RequestExecutionHandler) {
    if (!this.requestExecutionHandlers.has(key)) {
      this.requestExecutionHandlers.set(key, handler);
      this.registerRequests();
    }
  }

  removeExecutionHandler(key: string) {
    if (!this.requestExecutionHandlers.has(key)) return;

    // Aborts the current request in case it's executing.
    this.abortRequest(key);
    this.requestExecutionHandlers.delete(key);
  }

  private registerRequests() {
    const handlerEntries = this.requestExecutionHandlers.entries();
    for (const [key, executionHandler] of handlerEntries) {
      if (this.requestStateMap.has(key)) continue;
      this.requestStateMap.set(key, {
        executionHandler,
        requests: {},
      });
    }
  }

  getState(key: string): RequestState | void {
    return this.requestStateMap.get(key);
  }

  getCurrentRequestMetadata(key: string): RequestMetadata | void {
    const state = this.getState(key);
    if (!state || !state.currentRequest) return;

    const current = state.requests[state.currentRequest];
    if (!current) return;

    return current;
  }

  addHook(hook: (state?: RequestState) => void) {
    this.executionHooks.push(hook);
  }

  abortRequest(key: string) {
    const metadata = this.getCurrentRequestMetadata(key);
    if (!metadata || metadata.aborted) return;

    metadata.controller.abort();
    metadata.aborted = true;
  }

  async executeRequest<R = unknown>(
    key: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...args: any[]
  ): Promise<R | void> {
    const allowConcurrentRequests =
      this.requestExecutionHandlers.get(key)?.allowConcurrentRequests ?? false;
    const state = this.getState(key);
    if (!state) return;

    if (!allowConcurrentRequests && state.currentRequest) {
      this.abortRequest(key);
    }

    const nextRequestId = this.idGenerator.next().value;
    const controller = new AbortController();

    state.requests[nextRequestId] = {
      controller,
      timestamp: new Date(),
      aborted: false,
    };

    state.currentRequest = nextRequestId;

    const response = await state.executionHandler(controller.signal, ...args);

    this.executionHooks.forEach(hook => {
      hook(state);
    });

    return response as R;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  executeAllRequests(...args: any[]) {
    for (const key of this.requestStateMap.keys()) {
      this.executeRequest(key, ...args);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async executeAllAsync<R = unknown>(...args: any[]): Promise<void> {
    const executions: Promise<R | void>[] = [];
    for (const key of this.requestStateMap.keys()) {
      executions.push(this.executeRequest(key, ...args));
    }

    await Promise.all(executions);
  }

  static create(
    requestExecutionHandlers: Record<string, RequestExecutionHandler>
  ): RequestHandler {
    return new RequestHandler(
      new Map(Object.entries(requestExecutionHandlers))
    );
  }
}
